import { Space, Typography, Select } from "antd";
import { useContext, useEffect, useState } from "react";
import SeasonService from "../../services/Season";
import { useTranslation } from "react-i18next";
import { upperCaseFirstLetter } from "../../utilities/format";
import { GlobalContext } from "../../App";
import Stylesheet from "reactjs-stylesheet";
import moment from "moment";

const itemsPerColumn = 5;
const itemHeight = 40; // Adjust based on your item's height

const CropMapLegendSeasons = () => {
  const { t, i18n } = useTranslation("translation");

  const [seasons, setSeasons] = useState(null);
  const [filteredSeasons, setFilteredSeasons] = useState(null);

  const {
    selected_map_layer,
    activeSeasons,
    setActiveSeasons,
    seasonFilters,
    setSeasonFilters
  } = useContext(GlobalContext)

  useEffect(() => {
    const fetchSeasons = async () => { 
      const _seasons = await SeasonService.getSeasons()

      setSeasons(_seasons)
      
      // Set default active seasons (unfinished: endDate > now)
      const currentDate = moment();
      const defaultActiveSeasons = new Set(
        _seasons
          .filter(season => moment(season.endDate).isAfter(currentDate))
          .map(season => season.id)
      );
      

      setActiveSeasons(defaultActiveSeasons);
    }

    fetchSeasons()
  }, [setActiveSeasons])


  // Filter seasons based on filters
  useEffect(() => {
    if (!seasons) return;
    
    let filtered = seasons.filter(season => {
      const currentDate = moment();
      const isActive = moment(season.endDate).isAfter(currentDate) && moment(season.startDate).isBefore(currentDate);
      
      // Year filter
      if (seasonFilters.year && moment(season.endDate).year() !== seasonFilters.year) {
        return false;
      }
      
      // Crop filter
      if (seasonFilters.crop && season.planted_crop.name !== seasonFilters.crop) {
        return false;
      }
      
      // Status filter
      if (seasonFilters.status === 'active' && !isActive) {
        return false;
      }
      if (seasonFilters.status === 'inactive' && isActive) {
        return false;
      }
      
      return true;
    });
    
    // Sort by start date (most recent first)
    filtered.sort((a, b) => {
      return moment(b.startDate).diff(moment(a.startDate));
    });
    
    setFilteredSeasons(filtered);
  }, [seasons, seasonFilters]);

  const toggleSeason = (seasonId) => {
    const newActiveSeasons = new Set(activeSeasons);
    if (newActiveSeasons.has(seasonId)) {
      newActiveSeasons.delete(seasonId);
    } else {
      newActiveSeasons.add(seasonId);
    }
    setActiveSeasons(newActiveSeasons);
  };

  const getYearOptions = () => {
    if (!seasons) return [];
    const years = [...new Set(seasons.map(season => moment(season.endDate).year()))];
    return years.sort().map(year => ({ label: year, value: year }));
  };

  const getCropOptions = () => {
    if (!seasons) return [];
    const crops = [...new Set(seasons.map(season => season.planted_crop.name))];
    return crops.map(crop => ({ label: upperCaseFirstLetter(t(`components.crops.${crop}`)), value: crop }));
  };
    
  return (
    <>
      <div style={{backgroundColor: "transparent", left: 0, bottom: 0, overflow: "scroll", pointerEvents: "auto"}}>
        {/* Filter Controls */}
        <div style={{ 
          marginBottom: 8, 
          padding: '4px 8px', 
          backgroundColor: 'rgba(255,255,255,0.9)', 
          borderRadius: 4,
          display: 'flex',
          gap: 4,
          width: 'fit-content',
          maxWidth: '300px'
        }}>
          <Select
            placeholder="Year"
            size="small"
            allowClear
            style={{ width: '80px' }}
            options={getYearOptions()}
            value={seasonFilters.year}
            onChange={(value) => setSeasonFilters(prev => ({ ...prev, year: value }))}
          />
          <Select
            placeholder="Crop"
            size="small"
            allowClear
            style={{ width: '100px' }}
            options={getCropOptions()}
            value={seasonFilters.crop}
            onChange={(value) => setSeasonFilters(prev => ({ ...prev, crop: value }))}
          />
          <Select
            placeholder="Status"
            size="small"
            style={{ width: '80px' }}
            value={seasonFilters.status}
            options={[
              { label: 'All', value: 'all' },
              { label: 'Active', value: 'active' },
              { label: 'Inactive', value: 'inactive' }
            ]}
            onChange={(value) => setSeasonFilters(prev => ({ ...prev, status: value }))}
          />
        </div>
        
        {/* Legend Items */}
        <Space direction="vertical" size={"small"} styles={{item: {padding: 0, margin: 0, lineHeight: "1rem", ...styles.containerStyle}}} style={{rowGap: 0}}>
          {
            filteredSeasons?.map((season) => {
              const isActive = activeSeasons.has(season.id);
              const legend_item_name = `${season.name} (${t(`components.cropclasses.${season.planted_crop.cropClass}`)} / ${upperCaseFirstLetter(t(`components.crops.${season.planted_crop.name}`))})`
              
              return (
                <Space 
                  key={season.id}
                  direction="horizontal" 
                  style={{
                    columnGap: 0, 
                    ...styles.itemStyle,
                    cursor: 'pointer',
                    opacity: isActive ? 1 : 0.5
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleSeason(season.id);
                  }}
                >
                  <div 
                    className="color" 
                    style={{
                      backgroundColor: isActive ? season.color : '#666',
                      width: 16,
                      height: 16,
                      marginRight: 4
                    }}
                  ></div>
                  <Typography.Text 
                    className="label" 
                    style={{
                      color: isActive 
                        ? (selected_map_layer.name === "Google Satellite" ? "white" : "black")
                        : (selected_map_layer.name === "Google Satellite" ? "#ccc" : "#666")
                    }}
                  >
                    {legend_item_name}
                  </Typography.Text>
                </Space>
              ) 
            })
          }
        </Space>
      </div>
    </>
  )
}


const styles = Stylesheet.create({
  containerStyle: {
    display: 'flex',
    flexWrap: 'wrap',
    breakInside: "avoid",
    maxHeight: `${itemsPerColumn * itemHeight}px`,
    alignContent: 'flex-start',
  },
  
  itemStyle: {
    //width: '200px', // Adjust based on desired column width
    //height: `${itemHeight}px`,
  }
})

export default CropMapLegendSeasons;

